import './theme/theme.scss'
import { AppState, Auth0Provider, Auth0ProviderOptions } from '@auth0/auth0-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from 'react-router-dom'

import { TooltipProvider } from './components/ui/tooltip'
import { queryClient } from './lib/queryClient'
import { MockAuth0Provider } from './utils/mock-auth0'
import { routes } from './utils/routes'
import { isMockAuth } from './lib/authMode'
import { useIdleTokenRefresh } from './hooks/useIdleTokenRefresh'

const App = () => {
  const onRedirectCallback = (appState?: AppState) => {
    routes.navigate((appState && appState.returnTo) || window.location.pathname)
  }

  const useMockAuth = isMockAuth
  const isDevelopment = import.meta.env.MODE === 'development'

  // Use mock auth for local development
  if (useMockAuth) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <MockAuth0Provider>
            <IdleTokenRefreshWrapper>
              <RouterProvider router={routes} />
            </IdleTokenRefreshWrapper>
          </MockAuth0Provider>
        </TooltipProvider>
        <ReactQueryDevtools initialIsOpen={true} />
      </QueryClientProvider>
    )
  }

  // Use real Auth0 for production
  const domain = import.meta.env.VITE_APP_AUTH0_DOMAIN ?? ''
  const auth0Config: Auth0ProviderOptions = {
    domain,
    clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID ?? '',
    useRefreshTokens: true,
    authorizationParams: {
      audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
      redirect_uri: window.location.origin,
    },
    onRedirectCallback,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <Auth0Provider {...auth0Config}>
          <IdleTokenRefreshWrapper>
            <RouterProvider router={routes} />
          </IdleTokenRefreshWrapper>
        </Auth0Provider>
      </TooltipProvider>
      {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

/**
 * Wrapper component that handles idle token refresh
 */
const IdleTokenRefreshWrapper = ({ children }: { children: React.ReactNode }) => {
  // Enable idle token refresh for production (not mock auth)
  useIdleTokenRefresh(10) // Refresh after 10 minutes of idle time

  return <>{children}</>
}

export default App
